import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });

function StaleRow({ row, checked, onToggle }) {
  const diff = (row.new.total || 0) - (row.old.total || 0);
  return (
    <label className="flex gap-3 bg-white rounded-xl border border-zinc-200 p-3 cursor-pointer" data-testid={`stale-row-${row.lead_id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1" data-testid={`stale-check-${row.lead_id}`} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2">
          <span className="font-semibold text-zinc-900 truncate">{row.full_name || "Unknown"}</span>
          <span className={`text-xs font-semibold ${diff < 0 ? "text-green-700" : "text-amber-700"}`}>
            {diff > 0 ? "+" : ""}{fmt(diff)}
          </span>
        </div>
        <div className="text-[11px] text-zinc-400 truncate">{row.estimate_number || row.lead_id} · {row.estimate_status || "new"}</div>
        {row.possible_hidden_promo && (
          <div className="mt-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid={`stale-promo-warning-${row.lead_id}`}>
            Price would go UP. This may be a promo discount that was never saved as a discount. Not selected; check before fixing.
          </div>
        )}
        <div className="mt-2 grid grid-cols-[1fr_auto_1fr] gap-2 text-xs items-center">
          <div className="text-zinc-500">
            <div>Discount {fmt(row.old.discount_amount)}</div>
            <div>Tax {fmt(row.old.tax)}</div>
            <div className="font-semibold text-zinc-700 line-through">{fmt(row.old.total)}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-zinc-300" />
          <div className="text-zinc-700">
            <div>Discount {fmt(row.new.discount_amount)}</div>
            <div>Tax {fmt(row.new.tax)}</div>
            <div className="font-bold text-[#207024]" data-testid={`stale-new-total-${row.lead_id}`}>{fmt(row.new.total)}</div>
          </div>
        </div>
      </div>
    </label>
  );
}

export default function FixEstimateTotals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/estimates/stale-totals`, auth()).then((r) => {
      setRows(r.data.estimates);
      setSelected(new Set(r.data.estimates.filter((e) => !e.possible_hidden_promo).map((e) => e.lead_id)));
    }).catch(() => { setRows([]); toast.error("Couldn't load estimates"); });

  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const fix = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/estimates/fix-stale-totals`, { lead_ids: [...selected] }, auth());
      toast.success(`Fixed ${data.fixed} estimate${data.fixed === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error("Fix failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        <button onClick={() => navigate("/settings")} className="flex items-center gap-1 text-sm text-zinc-600" data-testid="back-to-settings">
          <ArrowLeft className="w-4 h-4" /> Settings
        </button>
        <h1 className="text-xl font-bold text-zinc-900">Fix Estimate Totals</h1>
        <p className="text-xs text-zinc-500">
          Open estimates whose saved discount, tax or total doesn't match their own line items (mostly from the old
          Messages quote editor). Converted estimates are never touched. Review, uncheck any you want to keep, then fix.
        </p>
        {rows === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-200 p-6 text-center" data-testid="stale-empty">
            <CheckCircle2 className="w-8 h-8 text-[#207024] mx-auto mb-2" />
            <div className="font-semibold text-zinc-900">All open estimates add up correctly</div>
          </div>
        ) : (
          <>
            <div className="text-sm text-zinc-700" data-testid="stale-count">{rows.length} estimate{rows.length === 1 ? "" : "s"} need fixing</div>
            {rows.map((r) => (
              <StaleRow key={r.lead_id} row={r} checked={selected.has(r.lead_id)} onToggle={() => toggle(r.lead_id)} />
            ))}
            <button onClick={fix} disabled={saving || selected.size === 0}
              className="sticky bottom-4 w-full h-12 rounded-full bg-[#207024] text-white font-semibold disabled:opacity-50 shadow-lg"
              data-testid="fix-stale-totals-btn">
              {saving ? "Fixing..." : `Fix ${selected.size} selected`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
